import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormatPriceForMWh } from '../../../../utils/formatters';
import { PriceTimer } from '../../../../components/PriceTimer/PriceTimer';
import userRoles from '../../../../constants/userRoles';
import { transactionTypeDetermination } from '../../utils/transactionType';

const DesktopContainer = styled.div`
  display: flex;

  @media all and (width <= 700px) {
    display: none;
  }
`;

const MobilContainer = styled.div`
  display: flex;

  @media all and (width > 700px) {
    display: none;
  }
`;


const FooterPrice = ({ product, manualPrice, releasing, timeLeft }) => {
  const FOOTER_LABEL_PREFIX = releasing ? `app.contract.sale.footer` : `app.contract.buy.footer`;

  const intl = useIntl();
  const productPriceObj = releasing ? product?.salePrice : product?.price;
  const priceSource = productPriceObj.priceSource;

  let priceSourceIntlId;
  switch (priceSource) {
    case 'BESTASK':
      priceSourceIntlId = `${FOOTER_LABEL_PREFIX}.bestAsk`;
      break;
    case 'BESTBID':
      priceSourceIntlId = `${FOOTER_LABEL_PREFIX}.bestBid`;
      break;
    default:
      priceSourceIntlId = `${FOOTER_LABEL_PREFIX}.settlement`;
      break;
  }

  const formatPriceForMWh = useFormatPriceForMWh(product.currency.id);

  const price = (manualPrice || productPriceObj.price);
  const tradeMessage = `${FOOTER_LABEL_PREFIX}.tradeMessage`;

  return (
    <>
      <DesktopContainer>
        <div className={'label'}>
          {intl.formatMessage(
            {
              id: tradeMessage,
            },
            {
              priceType: intl.formatMessage({ id: priceSourceIntlId }),
            },
          )}
        </div>
        <div className={'price'}>{formatPriceForMWh(price)}</div>
        <PriceTimer className={'priceTimer'} time={timeLeft} />
      </DesktopContainer>
      <MobilContainer>
        <div>
          <div className={'label'}>
            {intl.formatMessage({ id: 'app.lastPurchaseSteps.stockPrice' })}
          </div>
          <div className={'price'}>{formatPriceForMWh(price)}</div>
        </div>
        <PriceTimer className={'priceTimer'} time={timeLeft} />
      </MobilContainer>
    </>
  );
};

FooterPrice.propTypes = {
  product: PropTypes.object.isRequired,
  manualPrice: PropTypes.number,
  manualRate: PropTypes.number,
  releasing: PropTypes.bool,
  timeLeft: PropTypes.number,
};

const enabledForRoles = [userRoles.ADMIN, userRoles.CUSTOMER, userRoles.TRADER, userRoles.CUSTOMER_MULTIPLE_COTRACTS];

export const ValidFooterBody = ({ buyInfo, product, releasing, isFix, manualPrice, manualRate, onBuy, role, timeLeft, remainingPurchaseCount }) => {
  const transactionType = transactionTypeDetermination(isFix, releasing);
  const FOOTER_LABEL_PREFIX = `app.contract.${transactionType.toLowerCase()}.footer`;

  const intl = useIntl();

  const [disabled, setDisabled] = useState(!(buyInfo && enabledForRoles.indexOf(role) !== -1 && (!!manualPrice || timeLeft > 0) &&
    (remainingPurchaseCount > 0 || role === userRoles.ADMIN)));

  useEffect(() => {
    setDisabled(!(buyInfo && enabledForRoles.indexOf(role) !== -1 && (!!manualPrice || timeLeft > 0) &&
      (remainingPurchaseCount > 0 || role === userRoles.ADMIN)));
  }, [buyInfo, role, timeLeft, manualPrice, remainingPurchaseCount]);

  return (
    <>
      <div className={'priceAndTime'}>
        {product ? (
          <FooterPrice product={product} manualPrice={manualPrice} manualRate={manualRate} releasing={releasing} timeLeft={timeLeft} />
        ) : (
          <FormattedMessage className={'label'} id={`${FOOTER_LABEL_PREFIX}.selectProduct`} />
        )}
      </div>
      <Button className={'actionButton'} variant="contained" color="primary" onClick={onBuy} disabled={disabled}>
        {intl.formatMessage({
          id: `${FOOTER_LABEL_PREFIX}.tradeButton`,
        })}
      </Button>
    </>
  );
};

ValidFooterBody.propTypes = {
  onBuy: PropTypes.func.isRequired,
  buyInfo: PropTypes.object,
  product: PropTypes.object,
  releasing: PropTypes.bool,
  isFix: PropTypes.bool,
  manualPrice: PropTypes.number,
  manualRate: PropTypes.number,
  role: PropTypes.string,
  timeLeft: PropTypes.number,
  remainingPurchaseCount: PropTypes.number,
};
